import classNames from 'classnames'
import React, { useCallback, useState } from 'react'

import { STATSIG_EXPERIMENTS_CONFIG } from '@src/config/statsig-experiments.config'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { nonPhysicalProductDeliveryText } from '@src/constants/text.constants'
import { STATSIG_EXPERIMENTS, useStatsigExperiment } from '@src/experiments/statsig-integration'
import { QUINCE_BOT_LOCATORS } from '@src/quince-site-common/src/constants/quinceBot'
import Product from '@src/quince-site-common/src/domains/Product'
import productHelper, {
  CUSTOM_FIELD_NAMES,
  getFormattedCustomFieldText,
} from '@src/quince-site-common/src/helpers/productHelper'
import { useCart } from '@src/quince-site-common/src/hooks/cart'
import { useTrackIsElementVisible } from '@src/quince-site-common/src/hooks/useTrackIsElementVisible'
import AuthUtil from '@src/quince-site-common/src/utils/authUtil'
import { ANALYTIC_COOKIES } from '@src/quince-site-common/src/utils/constants'
import getCookie from '@src/quince-site-common/src/utils/getCookie'
import validateEmail from '@src/quince-site-common/src/utils/validateEmail'

import { Button, BUTTON_SIZE, BUTTON_TYPES } from '../button'
import { EDDMessageContainer } from '../eddMessageContainer'
import { VariantPurchaseInfo } from '../productForm/ProductForm'

import styles from './productCTA.module.scss'
import { CTA_TYPE } from './productCTAConstants'
import { useGoldBarProductEligibility } from './useGoldBarProductEligibility'
import { getIsGiftCard, holidayCutoffMsg, inStockMsg, isCurrentDateInHolidayCutoff } from './utils'

interface DisplayButtonType {
  onClick: () => void
  className?: string
  showLoader?: boolean
  selectorKey?: string
  children?: string
  buttonRef?: any
  disabled?: boolean
  buttonType?: BUTTON_TYPES
}

function getTrackingObject(key: string, selectedVariantId?: number | undefined) {
  return {
    action: key,
    value: true,
    context: {
      productId: selectedVariantId,
    },
  }
}

const DisplayButton = ({
  children,
  onClick,
  className,
  showLoader,
  selectorKey,
  buttonRef,
  disabled = false,
  buttonType = BUTTON_TYPES.PRIMARY,
}: DisplayButtonType) => {
  return (
    <Button
      className={classNames(styles.displayButtonWrapper, className, disabled && styles.productCTADisabled)}
      type={buttonType}
      size={BUTTON_SIZE.LARGE}
      disabled={disabled}
      round={true}
      onClick={onClick}
      showLoader={showLoader}
      selectorKey={selectorKey}
      buttonRef={buttonRef}
    >
      {children}
    </Button>
  )
}

interface RenderErrorMessageType {
  errorMessage: React.ReactNode
  className?: string
  isQuickATCForMobile?: boolean
}

export const RenderErrorMessage = ({ errorMessage, className, isQuickATCForMobile }: RenderErrorMessageType) => {
  return (
    <div className={classNames(styles.errorMessage, className, isQuickATCForMobile && styles.errorMessage__quickATC)}>
      {errorMessage}
    </div>
  )
}

const NonPhysicalProductDeliveryText = ({
  inStockMsgClass,
  isQuickATCForMobile,
}: {
  inStockMsgClass?: string
  isQuickATCForMobile?: boolean
}) => {
  return (
    <div
      className={classNames(
        inStockMsgClass,
        styles.inStockAndReadyToShipMessage,
        isQuickATCForMobile && styles.inStockAndReadyToShipMessage__quickATC,
      )}
    >
      {nonPhysicalProductDeliveryText}
    </div>
  )
}

export const InStockAndReadyToShip = ({
  inStockMsgClass,
  showHolidayCutoffMsg,
  isQuickATCForMobile,
}: {
  inStockMsgClass?: string
  showHolidayCutoffMsg?: boolean
  isQuickATCForMobile?: boolean
}) => {
  return (
    <div
      className={classNames(
        inStockMsgClass,
        styles.inStockAndReadyToShipMessage,
        isQuickATCForMobile && styles.inStockAndReadyToShipMessage__quickATC,
      )}
    >
      {showHolidayCutoffMsg ? holidayCutoffMsg : inStockMsg}
    </div>
  )
}

interface DisplayTextType {
  date?: string
  className?: string
  isQuickATCForMobile?: boolean
}

const DisplayText = ({ date, className, isQuickATCForMobile }: DisplayTextType) => {
  return (
    <div
      className={classNames(
        styles.displayTextWrapper,
        className,
        isQuickATCForMobile && styles.displayTextWrapper__quickATC,
      )}
    >
      Out of Stock {date ? `| Back in Stock after ${date}` : ''}
    </div>
  )
}

const getIsProductExcludedForReadyToShip = (product: Product | undefined) => {
  // const productsExcludedForReadyToShipMessage: number[] = []  commented since it is unnecessary without any product ids added to it
  return !product
}

interface ProductCTAType {
  onAddToCart: (isPreorder?: boolean) => void
  isAddToCartInProgress?: boolean
  backInStockDate?: boolean
  selectedVariantPurchaseInfo?: VariantPurchaseInfo
  onJoinWaitList?: (email?: string) => void
  type?: any
  showVariantAvailableInventory?: boolean
  product?: Product
  inStockMsgClass?: string
  areAllVariantOOS?: boolean
  addToCartRef?: any
  onBuyNow?: (isPreorder?: boolean) => void
  isQuickATCForMobile?: boolean
  isKeyboardVisibleForQuickATCForMobile?: boolean
  setIsKeyboardVisibleForQuickATCForMobile?: (isKeyboardVisible: boolean) => void
  isPostOrderFlow?: boolean
  handlePostOrderCallback?: () => void
}

const ProductCTA = (props: ProductCTAType) => {
  const {
    selectedVariantPurchaseInfo,
    onJoinWaitList,
    type,
    showVariantAvailableInventory,
    product,
    inStockMsgClass,
    areAllVariantOOS = false,
    addToCartRef,
    onBuyNow,
  } = props
  const selectedVariantId = selectedVariantPurchaseInfo?.selectedVariantId
  const isNonPhysicalProduct = product
    ? productHelper.getIsTruthyCustomField(product, CUSTOM_FIELD_NAMES.IS_NON_PHYSICAL_PRODUCT)
    : false
  const isMadeToOrder = product
    ? getFormattedCustomFieldText(
        productHelper.getCustomFieldValue(product, CUSTOM_FIELD_NAMES.MADE_TO_ORDER_TEXT) ?? '',
      )
    : false
  const isPreorder = selectedVariantPurchaseInfo?.isPreorder
  const isExcludedForReadyToShipMessage = getIsProductExcludedForReadyToShip(props.product)

  const [email, setEmail] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const eddExp = useStatsigExperiment(STATSIG_EXPERIMENTS.EDD)
  const isEDDExpEnabled =
    eddExp.experimentBucket !== STATSIG_EXPERIMENTS_CONFIG[STATSIG_EXPERIMENTS.EDD].buckets?.control

  const { isGoldBarError } = useGoldBarProductEligibility(product, setErrorMessage)
  const showHolidayCutoffMsg = isCurrentDateInHolidayCutoff() && !getIsGiftCard(product?.id)

  const { isBuyNowInProgress } = useCart()

  const showBuyNowButton = typeof onBuyNow === 'function'

  const showInStockAndReadyToShip =
    selectedVariantPurchaseInfo?.isAnySelectedVariantInStock &&
    !showVariantAvailableInventory &&
    !isPreorder &&
    !isMadeToOrder &&
    !isExcludedForReadyToShipMessage &&
    !areAllVariantOOS &&
    !isGoldBarError &&
    !isNonPhysicalProduct &&
    !isEDDExpEnabled &&
    eddExp.init

  const EMAIL_ERROR_MSG = 'Please enter valid email address'
  const ctaText = showBuyNowButton ? 'ADD TO CART' : 'PREORDER'
  const disableClickATC = props.isAddToCartInProgress && 'pointer-events-none'
  // Reset invalid email address message when input element is absent.
  const revertEmailErrorMsg = () => {
    if (errorMessage === EMAIL_ERROR_MSG) {
      setErrorMessage('')
    }
  }

  const { ref: trackIsVisiblePreorderRef } = useTrackIsElementVisible(
    useCallback(() => getTrackingObject('preorder_button', selectedVariantId), [selectedVariantId]),
  )
  const { ref: trackIsVisibleWaitlistRef } = useTrackIsElementVisible(
    useCallback(() => getTrackingObject('waitlist_button', selectedVariantId), [selectedVariantId]),
  )

  const handleClick = () => {
    switch (type) {
      case CTA_TYPE.PREORDER:
        props.onAddToCart(true)
        break

      case CTA_TYPE.WAITLIST:
        if (selectedVariantPurchaseInfo?.hasJoinedWaitList) {
          return
        }
        if (AuthUtil.isLoggedIn()) {
          const email =
            getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_EMAIL) ??
            getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_TENTATIVE_EMAIL) ??
            ''
          onJoinWaitList?.(email)
        } else {
          if (validateEmail(email)) {
            onJoinWaitList?.(email)
            setErrorMessage('')
          } else {
            setErrorMessage(EMAIL_ERROR_MSG)
          }
        }
        break

      case CTA_TYPE.ADD_TO_CART:
        props.isPostOrderFlow && props.handlePostOrderCallback ? props.handlePostOrderCallback() : props.onAddToCart()
        break

      default:
        console.warn('Invalid type')
    }
  }

  switch (type) {
    case CTA_TYPE.PREORDER: {
      if (props.isPostOrderFlow) {
        return (
          <DisplayButton onClick={() => undefined} disabled={true} className={`width--full pointer-events-none`}>
            Out of Stock
          </DisplayButton>
        )
      }
      revertEmailErrorMsg()
      return (
        <div data-testid={TEST_LOCATORS.PRODUCT_CTA} ref={trackIsVisiblePreorderRef} className={styles.buttonWrapper}>
          {errorMessage && (
            <RenderErrorMessage errorMessage={errorMessage} isQuickATCForMobile={props.isQuickATCForMobile} />
          )}

          {props.isQuickATCForMobile ? (
            !props.isKeyboardVisibleForQuickATCForMobile && (
              // Only show the sticky button when keyboard is not visible
              <div className={styles.quickATCWrapper}>
                <DisplayButton
                  onClick={handleClick}
                  showLoader={props.isAddToCartInProgress}
                  selectorKey={QUINCE_BOT_LOCATORS.PDP.ADD_TO_CART}
                >
                  {ctaText}
                </DisplayButton>
              </div>
            )
          ) : (
            // Regular button for non-quickATC mode
            <DisplayButton
              onClick={handleClick}
              showLoader={props.isAddToCartInProgress}
              selectorKey={QUINCE_BOT_LOCATORS.PDP.ADD_TO_CART}
            >
              {ctaText}
            </DisplayButton>
          )}

          {showBuyNowButton && (
            <DisplayButton
              onClick={() => {
                onBuyNow(true)
              }}
              showLoader={isBuyNowInProgress}
              selectorKey={QUINCE_BOT_LOCATORS.PDP.BUY_NOW}
              buttonType={BUTTON_TYPES.OUTLINED}
              className={classNames('margin-top--2', disableClickATC)}
            >
              PREORDER NOW
            </DisplayButton>
          )}
        </div>
      )
    }

    case CTA_TYPE.WAITLIST: {
      if (props.isPostOrderFlow) {
        return (
          <DisplayButton onClick={() => undefined} disabled={true} className={`width--full pointer-events-none`}>
            Out of Stock
          </DisplayButton>
        )
      }
      return (
        <div data-testid={TEST_LOCATORS.PRODUCT_CTA} ref={trackIsVisibleWaitlistRef} className={styles.buttonWrapper}>
          <DisplayText
            date={selectedVariantPurchaseInfo?.backInStockDate}
            className={styles.waitListed__text}
            isQuickATCForMobile={props.isQuickATCForMobile}
          />
          {!selectedVariantPurchaseInfo?.hasJoinedWaitList && !AuthUtil.isLoggedIn() && (
            <input
              onChange={e => {
                setEmail(e.target.value)
              }}
              onFocus={() => {
                props.setIsKeyboardVisibleForQuickATCForMobile?.(true)
              }}
              onBlur={() => {
                props.setIsKeyboardVisibleForQuickATCForMobile?.(false)
              }}
              className={classNames(styles.waitlistInput, props.isQuickATCForMobile && styles.waitlistInput__quickATC)}
              placeholder={props.isQuickATCForMobile ? 'Enter Your Email Address' : 'Your Email Address'}
              type="email"
            />
          )}
          {errorMessage && (
            <RenderErrorMessage errorMessage={errorMessage} isQuickATCForMobile={props.isQuickATCForMobile} />
          )}

          {props.isQuickATCForMobile ? (
            !props.isKeyboardVisibleForQuickATCForMobile && (
              // Only show the sticky button when keyboard is not visible
              <div className={styles.quickATCWrapper}>
                {selectedVariantPurchaseInfo?.hasJoinedWaitList ? (
                  <DisplayButton onClick={handleClick} className={styles.waitlisted}>
                    WAITLISTED
                  </DisplayButton>
                ) : (
                  <DisplayButton onClick={handleClick}>JOIN WAITLIST</DisplayButton>
                )}
              </div>
            )
          ) : // Regular button for non-quickATC mode
          selectedVariantPurchaseInfo?.hasJoinedWaitList ? (
            <DisplayButton onClick={handleClick} className={styles.waitlisted}>
              WAITLISTED
            </DisplayButton>
          ) : (
            <DisplayButton onClick={handleClick}>JOIN WAITLIST</DisplayButton>
          )}
        </div>
      )
    }

    default:
      revertEmailErrorMsg()
      return (
        <div data-testid={TEST_LOCATORS.PRODUCT_CTA}>
          {isNonPhysicalProduct && (
            <NonPhysicalProductDeliveryText
              inStockMsgClass={inStockMsgClass}
              isQuickATCForMobile={props.isQuickATCForMobile}
            />
          )}
          {showInStockAndReadyToShip ? (
            <InStockAndReadyToShip
              inStockMsgClass={inStockMsgClass}
              showHolidayCutoffMsg={showHolidayCutoffMsg}
              isQuickATCForMobile={props.isQuickATCForMobile}
            />
          ) : null}
          {errorMessage && (
            <RenderErrorMessage errorMessage={errorMessage} isQuickATCForMobile={props.isQuickATCForMobile} />
          )}

          {props.isQuickATCForMobile ? (
            !props.isKeyboardVisibleForQuickATCForMobile && (
              // Only show the sticky button when keyboard is not visible
              <div className={styles.quickATCWrapper}>
                <DisplayButton
                  onClick={handleClick}
                  showLoader={props.isAddToCartInProgress}
                  selectorKey={QUINCE_BOT_LOCATORS.PDP.ADD_TO_CART}
                  buttonRef={addToCartRef}
                  disabled={isGoldBarError}
                  className={`width--full ${isBuyNowInProgress ? 'pointer-events-none' : ''}`}
                >
                  ADD TO CART
                </DisplayButton>
              </div>
            )
          ) : // Regular button for non-quickATC mode
          props.isPostOrderFlow ? (
            <DisplayButton
              onClick={handleClick}
              showLoader={props.isAddToCartInProgress}
              selectorKey={QUINCE_BOT_LOCATORS.PDP.ADD_TO_CART}
              buttonRef={addToCartRef}
              disabled={isGoldBarError}
              className={`width--full ${isBuyNowInProgress ? 'pointer-events-none' : ''}`}
            >
              Continue with this item
            </DisplayButton>
          ) : (
            <DisplayButton
              onClick={handleClick}
              showLoader={props.isAddToCartInProgress}
              selectorKey={QUINCE_BOT_LOCATORS.PDP.ADD_TO_CART}
              buttonRef={addToCartRef}
              disabled={isGoldBarError}
              className={`width--full ${isBuyNowInProgress ? 'pointer-events-none' : ''}`}
            >
              ADD TO CART
            </DisplayButton>
          )}

          {showBuyNowButton && (
            <DisplayButton
              onClick={onBuyNow}
              showLoader={isBuyNowInProgress}
              selectorKey={QUINCE_BOT_LOCATORS.PDP.BUY_NOW}
              buttonRef={addToCartRef}
              buttonType={BUTTON_TYPES.OUTLINED}
              className={classNames('margin-top--2 width--full', disableClickATC)}
            >
              Buy now
            </DisplayButton>
          )}
        </div>
      )
  }
}

const ProductCTAWrapper = (props: ProductCTAType) => {
  const showEDDMsg = !(
    props.type === CTA_TYPE.WAITLIST ||
    (props.selectedVariantPurchaseInfo?.isPreorder ??
      props.selectedVariantPurchaseInfo?.showMadeToOrder ??
      getIsGiftCard(props.product?.id))
  )
  return (
    <>
      {showEDDMsg ? (
        <EDDMessageContainer
          eddDetail={props.selectedVariantPurchaseInfo?.eddDetail}
          product={props.product}
          selectedInternalVariantId={props.selectedVariantPurchaseInfo?.selectedInternalVariantId}
          isQuickATCForMobile={props.isQuickATCForMobile}
        />
      ) : null}
      <ProductCTA {...props} />
    </>
  )
}
export default ProductCTAWrapper
